import { Link } from "react-router-dom";
import SEO from "../components/SEO";

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-xl md:text-2xl font-display font-bold text-forest mb-3">{title}</h2>
      <div className="text-charcoal-soft leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function EditorialPolicy() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <>
      <SEO
        title="Editorial Standards & Content Methodology | Pennsylvania Accident Aid"
        description="How Pennsylvania Accident Aid researches, sources, verifies, and edits its accident and injury resources — including primary-source and statistical sourcing standards."
        url={`${origin}/editorial-policy`}
      />
      <section className="pt-12 md:pt-20 pb-16 md:pb-24 px-6 bg-bone">
        <div className="max-w-3xl mx-auto" data-testid="editorial-policy">
          <div className="overline mb-3">Editorial Standards</div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-6">
            How we research and write
          </h1>
          <p className="text-lg text-charcoal-soft leading-relaxed mb-10">
            Pennsylvania Accident Aid publishes educational resources for accident and injury victims.
            Our goal is to be accurate, transparent, and genuinely useful. Here is how our content is made.
          </p>

          <Section title="How topics are researched">
            <p>Topics are chosen based on the real questions accident and injury victims in Pennsylvania ask.
            Each guide is developed from Pennsylvania-specific law and practice rather than generic national content.</p>
          </Section>
          <Section title="Primary-source preference">
            <p>We prefer primary sources: the Pennsylvania Consolidated Statutes, published Pennsylvania
            appellate decisions, and official government data. Secondary sources are used only to locate
            primary sources, never as a substitute for them.</p>
          </Section>
          <Section title="Legal & statutory sourcing">
            <p>Statutory citations (for example, 42 Pa.C.S. and 75 Pa.C.S. provisions) are checked against
            the official statute text. Case law is limited to a maintained registry of verified Pennsylvania
            authorities; an article cannot cite a case that is not verified.</p>
          </Section>
          <Section title="Statistical sourcing">
            <p>Every agency statistic must resolve to a verified entry in our statistics registry — with a
            named source (such as PennDOT Crash Facts, NHTSA/FARS, FHWA, or the CDC), a year, and a source
            link — or it is not published. Figures we cannot verify against an authoritative source are
            omitted rather than estimated.</p>
          </Section>
          <Section title="Factual verification">
            <p>Before publication, content passes automated checks that block unverified case citations,
            unsupported settlement/verdict claims, and agency statistics that are not in the verified
            registry.</p>
          </Section>
          <Section title="AI-assisted drafting, human editing">
            <p>We use AI tools to assist with drafting and editing. AI is a drafting aid only — a human
            editor is responsible for the research direction, source verification, and every published
            claim. Nothing publishes solely because a tool produced it.</p>
          </Section>
          <Section title="Human editorial responsibility">
            <p>Final responsibility for accuracy and tone rests with the editor,
            <Link to="/authors/aaron-bankhurst" className="text-forest font-semibold hover:text-terracotta transition-colors"> Aaron Bankhurst</Link>.
            Where a qualified professional has reviewed a specific article, that reviewer is named separately
            on the article — we never imply professional review that did not occur.</p>
          </Section>
          <Section title="How updates are handled">
            <p>When we make a substantive factual change to an article, we update its modified date. Minor
            fixes that do not change meaning do not reset the modified date. See our
            <Link to="/corrections" className="text-forest font-semibold hover:text-terracotta transition-colors"> corrections policy</Link>.</p>
          </Section>

          <p className="text-xs text-charcoal-soft mt-8 leading-relaxed">
            Pennsylvania Accident Aid is not a law firm and does not provide legal advice. Our content is
            educational and does not create an attorney-client relationship.
          </p>
        </div>
      </section>
    </>
  );
}
